<template>
  <event-page-layout :enable-scroll-event="true">
    <Loading v-if="isLoading" />
    <div v-else>
      <div :class="{ relative: event.thumbnail }">
        <BackButton class="px-[20px]" :class="{ 'absolute top-0 left-0 z-10': event.thumbnail }" />
        <EventPageThumbnail v-if="event.thumbnail" :thumbnail-url="event.thumbnail?.url" />
      </div>
      <div
        id="event-page-blocks-section"
        class="mb-[48px] pb-[120px]"
        :class="{ 'relative top-2': !event.thumbnail }"
      >
        <AnnouncementSection
          v-if="hasAnnouncementText"
          :class="event.thumbnail ? '-mt-6' : 'mt-[12px]'"
          :body-text="event.announcementText"
        />
        <EventPageBlockRenderer
          v-for="(block, index) in event.eventPageBlocks"
          :key="index"
          :block="block"
        />
        <div v-if="event.subPages?.length" class="mx-5 mt-6 flex justify-center">
          <div
            ref="subPageTabsContainer"
            class="inline-flex max-w-[90vw] rounded-full border p-1 shadow-sm transition-colors duration-200"
            :class="isLightMode ? 'bg-[#F2EFED] border-[#D6D3CF]' : 'bg-[#23211F] border-[#4B4640]'"
          >
            <div class="flex min-w-0 gap-1">
              <button
                v-for="(subPage, index) in event.subPages"
                :key="subPage.id || index"
                ref="subPageTabButtons"
                type="button"
                class="subpage-tab min-w-0 rounded-full text-center font-semibold whitespace-nowrap transition-colors duration-200 active:scale-[0.98] active:opacity-80"
                :class="[getSubPageTabClass(index), subPageTabSizeClass]"
                @click="setActiveSubPageIndex(index)"
              >
                <span class="block">{{ subPage.name }}</span>
              </button>
            </div>
          </div>
        </div>
        <div
          v-if="activeSubPage"
          ref="subPageContent"
          class="mt-2"
          data-disable-swipe-navigation="true"
          @touchstart.passive="onSubPageTouchStart"
          @touchend.passive="onSubPageTouchEnd"
        >
          <SubPageKeyVisual v-if="activeSubPage.keyVisual?.url" :image="activeSubPage.keyVisual" />
          <EventPageBlockRenderer
            v-for="(block, index) in activeSubPage.eventPageBlocks"
            :key="`${activeSubPage.id}-${index}`"
            :block="block"
          />
        </div>
      </div>
      <RegisterButton
        v-if="currentRegistrationLink"
        :label="currentRegistrationLink.label"
        :is-external="currentRegistrationLink.isExternal"
        :registration-url="currentRegistrationLink.URL"
        :event-id="event.id"
        :event-slug="$route.params.slug"
        :event-title="event.title || event.name"
      />
    </div>
  </event-page-layout>
</template>

<script>
import { createAnimation } from '@ionic/vue';
import { mapState } from 'vuex';
import EventPageLayout from '@/components/EventPageLayout.vue';
import EventPageThumbnail from '@/components/EventPage/EventPageThumbnail.vue';
import AnnouncementSection from '@/components/EventPage/AnnouncementSection.vue';
import Loading from '@/components/common/LoadingState.vue';
import BackButton from '@/components/common/BackButton.vue';
import RegisterButton from '@/components/EventPage/RegisterButton.vue';
import SubPageKeyVisual from '@/components/EventPage/EventKeyVisual.vue';
import EventPageBlockRenderer from '@/components/EventPage/EventPageBlockRenderer.vue';
import { openUrl } from '@/utils/browser-helper';
import { eventBus } from '@/utils/eventBus';
import { trackContentClick, trackEventViewed } from '@/utils/analytics';

export default {
  components: {
    EventPageLayout,
    EventPageThumbnail,
    AnnouncementSection,
    RegisterButton,
    Loading,
    BackButton,
    EventPageBlockRenderer,
    SubPageKeyVisual,
  },
  data() {
    return {
      activeSubPageIndex: 0,
      subPageTouchStartX: null,
      subPageTouchStartY: null,
      shouldIgnoreSubPageSwipe: false,
      isAnimatingSubPage: false,
      unsubscribeEventBus: null,
    };
  },
  computed: {
    ...mapState('eventPage', ['event', 'isLoading', 'isLightMode']),
    eventPageBlockTypes() {
      const blocks = this.event.eventPageBlocks || [];
      return blocks.map(b => b.__component);
    },
    hasAnnouncementText() {
      const text = this.event.announcementText;
      if (!text) return false;
      if (typeof text === 'string') return text.trim().length > 0;
      return this.richTextHasContent(text);
    },
    activeSubPage() {
      return this.event.subPages?.[this.activeSubPageIndex] || null;
    },
    subPageTabSizeClass() {
      const subPages = this.event.subPages || [];
      const count = subPages.length;

      // Calculate total character count of all tab names
      const totalChars = subPages.reduce((sum, sp) => sum + (sp.name?.length || 0), 0);
      const avgChars = count > 0 ? totalChars / count : 0;

      // Scale down based on tab count and average name length
      if (count >= 4 || (count >= 3 && avgChars > 10)) {
        return 'text-[0.6875rem] leading-[0.875rem] px-3 py-1.5';
      }
      if (count >= 3 || avgChars > 12) {
        return 'text-[0.75rem] leading-[1rem] px-3 py-2';
      }
      return 'text-[0.875rem] leading-[1.25rem] px-4 py-2';
    },
    currentRegistrationLink() {
      return this.activeSubPage?.registrationLink || this.event.registrationLink;
    },
  },
  methods: {
    richTextHasContent(node) {
      if (!node) return false;
      if (node.text?.trim().length > 0) return true;
      if (Array.isArray(node.content)) {
        return node.content.some(child => this.richTextHasContent(child));
      }
      return false;
    },
    getSubPageTabClass(index) {
      if (this.activeSubPageIndex === index) {
        return 'bg-[#FEC629] text-[#21201E] font-bold';
      }
      return this.isLightMode ? 'bg-transparent text-[#63615E]' : 'bg-transparent text-[#D6D3CF]';
    },
    async setActiveSubPageIndex(index) {
      if (
        this.isAnimatingSubPage ||
        index === this.activeSubPageIndex ||
        !this.event.subPages?.length
      ) {
        return;
      }

      const direction = index > this.activeSubPageIndex ? 'forward' : 'back';
      const selectedSubPage = this.event.subPages?.[index];
      trackContentClick({
        contentType: 'event_subpage',
        contentId: selectedSubPage?.id || selectedSubPage?.name,
        title: selectedSubPage?.name,
        sourceSection: 'event-page-tabs',
        destination: this.$route.path,
        isExternal: false,
        source: 'event-page-tabs',
      });
      const currentEl = this.$refs.subPageContent;

      if (!currentEl) {
        this.activeSubPageIndex = index;
        return;
      }

      this.isAnimatingSubPage = true;

      const exitTranslate = direction === 'forward' ? 'translateX(-18px)' : 'translateX(18px)';
      await createAnimation()
        .addElement(currentEl)
        .duration(180)
        .easing('cubic-bezier(0.32, 0.72, 0, 1)')
        .fromTo('opacity', '1', '0')
        .fromTo('transform', 'translateX(0px)', exitTranslate)
        .play();

      this.activeSubPageIndex = index;
      await this.$nextTick();

      const nextEl = this.$refs.subPageContent;
      if (!nextEl) {
        this.isAnimatingSubPage = false;
        return;
      }

      const enterTranslate = direction === 'forward' ? 'translateX(18px)' : 'translateX(-18px)';
      await createAnimation()
        .addElement(nextEl)
        .duration(220)
        .easing('cubic-bezier(0.2, 0.8, 0.2, 1)')
        .fromTo('opacity', '0', '1')
        .fromTo('transform', enterTranslate, 'translateX(0px)')
        .play();

      this.isAnimatingSubPage = false;
    },
    onSubPageTouchStart(event) {
      this.shouldIgnoreSubPageSwipe = this.shouldDisableSubPageSwipe(event.target);

      if (this.shouldIgnoreSubPageSwipe) {
        this.resetTouchState();
        return;
      }

      const touch = event.changedTouches?.[0];
      if (!touch) return;

      this.subPageTouchStartX = touch.clientX;
      this.subPageTouchStartY = touch.clientY;
    },
    onSubPageTouchEnd(event) {
      if (this.shouldIgnoreSubPageSwipe) {
        this.shouldIgnoreSubPageSwipe = false;
        this.resetTouchState();
        return;
      }

      const touch = event.changedTouches?.[0];
      if (!touch || this.subPageTouchStartX === null || this.subPageTouchStartY === null) {
        return;
      }

      const deltaX = touch.clientX - this.subPageTouchStartX;
      const deltaY = touch.clientY - this.subPageTouchStartY;
      const swipeThreshold = 48;

      this.resetTouchState();

      if (Math.abs(deltaX) < swipeThreshold || Math.abs(deltaX) <= Math.abs(deltaY)) {
        return;
      }

      const maxIndex = (this.event.subPages?.length || 1) - 1;
      if (deltaX < 0) {
        this.setActiveSubPageIndex(Math.min(this.activeSubPageIndex + 1, maxIndex));
      } else {
        this.setActiveSubPageIndex(Math.max(this.activeSubPageIndex - 1, 0));
      }
    },
    shouldDisableSubPageSwipe(target) {
      return (
        target instanceof Element && Boolean(target.closest('[data-disable-subpage-swipe="true"]'))
      );
    },
    resetTouchState() {
      this.subPageTouchStartX = null;
      this.subPageTouchStartY = null;
      this.shouldIgnoreSubPageSwipe = false;
    },
    async handleAction(action) {
      if (!action) return;

      const { buttonType, url, isExternal } = action;

      if (buttonType !== 'url' || !url) return;

      if (isExternal) {
        await openUrl(
          { url },
          { title: action.label || 'External Link', source: 'event-action', page: 'EventPage' },
        );
        return;
      }

      // Strip domain from internal URLs (e.g., https://mobile.heartofgodchurch.org/absolutely -> /absolutely)
      let internalPath;
      try {
        const urlObj = new URL(url);
        internalPath = urlObj.pathname;
      } catch {
        // If URL parsing fails, assume it's already a path
        internalPath = url.startsWith('/') ? url : `/${url}`;
      }
      this.$router.push(internalPath);
    },
  },
  async ionViewWillEnter() {
    if (this.$store.state.user.token) {
      await Promise.allSettled([
        this.$store.dispatch('profile/fetchProfile'),
        this.$store.dispatch('lookups/fetchPastoralMapping'),
      ]);
    }
    await this.$store.dispatch('eventPage/getEvent', this.$route.params.slug);
    trackEventViewed({
      eventId: this.event?.id,
      slug: this.$route.params.slug,
      title: this.event?.title || this.event?.name,
      source: 'event-page',
    });
    this.activeSubPageIndex = 0;
  },
  mounted() {
    this.unsubscribeEventBus = eventBus.on('event-page-action', this.handleAction);
  },
  beforeUnmount() {
    if (this.unsubscribeEventBus) {
      this.unsubscribeEventBus();
    }
  },
};
</script>
